import { useEffect, useRef, type ReactNode } from 'react'
import { Link } from '@inertiajs/react'
import styles from './Menu.module.css'

export interface MenuProps {
  label: ReactNode
  children: ReactNode
}

/* Dropdown on a native <details>; closes on outside click and Escape. */
export function Menu({ label, children }: MenuProps) {
  const ref = useRef<HTMLDetailsElement>(null)

  useEffect(() => {
    const el = ref.current
    if (!el) return
    const onDoc = (e: MouseEvent) => { if (el.open && !el.contains(e.target as Node)) el.open = false }
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') el.open = false }
    document.addEventListener('click', onDoc)
    document.addEventListener('keydown', onKey)
    return () => { document.removeEventListener('click', onDoc); document.removeEventListener('keydown', onKey) }
  }, [])

  return (
    <details ref={ref} className={styles.menu}>
      <summary className={styles.trigger}>{label}</summary>
      <div className={styles.panel} role="menu">{children}</div>
    </details>
  )
}

export function MenuLabel({ children }: { children: ReactNode }) {
  return <div className={styles.label}>{children}</div>
}

export function MenuDivider() {
  return <div className={styles.divider} role="separator" />
}

export interface MenuItemProps {
  href?: string
  onClick?: () => void
  children: ReactNode
}

export function MenuItem({ href, onClick, children }: MenuItemProps) {
  if (href) return <Link href={href} className={styles.item} role="menuitem">{children}</Link>
  return <button type="button" className={styles.item} role="menuitem" onClick={onClick}>{children}</button>
}
